<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import validatorMixin from '../../../mixins/validatorMixin';
import store from '../../../store';

export default {
  data() {
    return {
      name: '',
      headers: [
        { text: this.$t('skiFacts.dataHeaders.category'), sortable: false },
        { text: this.$t('skiFacts.dataHeaders.value'), sortable: false, width: '30%' },
        { text: this.$t('skiFacts.dataHeaders.unit'), sortable: false, width: '30%' },
      ],
      unit: null,
      fab: false,
      saveSuccess: false,
      snackbar: false,
      formValid: true,
    };
  },
  mixins: [validatorMixin],
  computed: {
    ...mapState({
      area: state => state.areas.area,
      items: state => state.areas.facts.items,
      loading: state => state.areas.facts.loading,
      error: state => state.areas.facts.error,
      units: state => state.areas.facts.units,
    }),
    ...mapGetters({
      factUnitItems: 'areas/factUnitItems',
      anyFactChecked: 'areas/anyFactChecked',
      checkFormValid: 'areas/checkFormValid',
    }),
  },
  methods: {
    ...mapActions({
      setSkiFact: 'areas/setSkiFact',
      checkValuedSkiFacts: 'areas/checkValuedSkiFacts',
      updateSkiFacts: 'areas/updateSkiFacts',
      checkAllSkiFacts: 'areas/checkAllSkiFacts',
    }),
    save() {
      if (this.checkFormValid) {
        this.updateSkiFacts({
          id: this.area.id,
          data: {
            facts: this.items,
          },
        })
          .then(() => {
            this.saveSuccess = true;
            this.snackbar = true;
            this.changed = false;
          })
          .catch(() => {
            this.saveSuccess = false;
            this.snackbar = true;
          });
      }
    },
    check() {
      this.changed = true;
      this.checkValuedSkiFacts();
    },
    update(index, oldValue, newValue) {
      const newValueUpdated = newValue;
      if (newValueUpdated.skiFactUnit) {
        if (newValueUpdated.skiFactUnit.id === this.$unitTypes.BOOLYES) {
          newValueUpdated.value = 1;
        } else if (newValue.skiFactUnit.id === this.$unitTypes.BOOLNO) {
          newValueUpdated.value = 0;
        }
      }
      this.$nextTick(this.$refs.form.validate);
      this.changed = true;
      this.setSkiFact({
        index,
        payload: { ...oldValue, ...newValueUpdated },
      });
    },
    checkAll() {
      if (this.anyFactChecked) {
        this.checkAllSkiFacts(false);
      } else {
        this.checkAllSkiFacts(true);
      }
      this.$nextTick(this.$refs.form.validate);
    },
    showFactValue(item) {
      return item.skiFactUnit === null
        || (
          item.skiFactUnit
          && item.skiFactUnit.id
          && item.skiFactUnit.id !== this.$unitTypes.BOOLYES
          && item.skiFactUnit.id !== this.$unitTypes.BOOLNO
        );
    },
  },
  beforeRouteEnter(to, from, next) {
    store.dispatch('areas/fetchSkiFacts', to.params.id)
      .then(() => {
        next();
      })
      .catch(() => {
        next();
      });
  },
  beforeRouteLeave(to, from, next) {
    return this.$changeDetect(this.changed, next);
  },
};
</script>

<template>
  <div class="ski-area-facts">
    <v-form
      :value="checkFormValid"
      ref="form">
      <v-speed-dial
        v-model="fab"
        bottom
        right
        fixed
        direction="top"
        transition="slide-y-reverse-transition">
        <template v-slot:activator>
          <v-btn
            v-model="fab"
            color="blue darken-2"
            dark
            fab>
            <v-icon>more_horiz</v-icon>
            <v-icon>close</v-icon>
          </v-btn>
        </template>
        <v-tooltip left nudge-left>
          <template v-slot:activator="{on}">
            <v-btn
              v-on="on"
              @click="save"
              :color="formValid ? 'green' : 'red'"
              fab
              dark
              small
              class="mb-3">
              <v-icon>save</v-icon>
            </v-btn>
          </template>
          <span v-if="checkFormValid">{{ $t('skiFacts.fab.save') }}</span>
          <span v-else>{{ $t('formInvalid') }}</span>
        </v-tooltip>
        <v-tooltip left nudge-left>
          <template v-slot:activator="{on}">
            <v-btn
              v-on="on"
              @click="check"
              fab
              dark
              small
              color="orange">
              <v-icon>check_box</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('skiFacts.fab.check') }}</span>
        </v-tooltip>
      </v-speed-dial>
      <v-data-table
        :headers="headers"
        :items="items || []"
        hide-actions
        class="">
        <template v-slot:headers="props">
          <tr>
            <th>
              <v-checkbox
                :input-value="anyFactChecked"
                :indeterminate="anyFactChecked"
                primary
                hide-details
                @click.stop="checkAll"/>
            </th>
            <th v-for="header in headers"
                :key="header.text"
                class="column text-xs-left">
              {{ header.text }}
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <td class="justify-center">
            <v-checkbox
              :input-value="props.item.show"
              :disabled="loading"
              @change="update(props.index, props.item, { show: $event })"
              tabindex="-1"
              primary
              hide-details
            />
          </td>
          <td class="font-weight-medium">
            {{ $t(`skiFacts.facts.${props.item.skiFactCategory.value}`)}}
          </td>
          <td>
            <v-text-field
              v-if="showFactValue(props.item)"
              :rules="[
                rules.requiredNumber.bind(this, props.item.show),
              ]"
              @input="update(props.index, props.item, { value: $event })"
              :value="props.item.value"
              :disabled="loading"
              :label="$t('none')"
              color="primary"
              single-line
              type="number"
            />
          </td>
          <td>
            <v-select
              :rules="[
                rules.requiredWith.bind(this, props.item.show),
              ]"
              @input="update(props.index, props.item, { skiFactUnit: { id: $event } })"
              :items="factUnitItems"
              :value="props.item.skiFactUnit ? props.item.skiFactUnit.id : null"
              :disabled="loading"
              :label="$t('none')"
              single-line
              clearable
              item-value="id"
              item-text="label"
            />
          </td>
        </template>
      </v-data-table>
      <v-snackbar
        v-model="snackbar"
        :timeout="4000"
        bottom>
        <v-icon v-if="saveSuccess" class="mr-3 green--text">check_circle</v-icon>
        <v-icon v-else class="mr-3 red--text">warning</v-icon>
        {{ saveSuccess
        ? $t('skiFacts.snackbar.save.success')
        : $t('skiFacts.snackbar.save.error')
        }}
        <v-btn
          @click="snackbar = false"
          color="white"
          flat>
          {{ $t('skiFacts.snackbar.save.button') }}
        </v-btn>
      </v-snackbar>
    </v-form>
  </div>
</template>



// WEBPACK FOOTER //
// src/pages/ski/areas/SkiAreaFacts.vue